import type { DateRange, FuelCardVehicle, FuelTransactionBodyContent, UUID } from '@cobliteam/common-types';
import type { Configuration } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import TimeZone from '../../request/Timezone';
import type { IRequest } from '../../request/Types';
import { fuelCardMatchingAPIToFuelCardMatching, fuelVehiclesTransformer, openNewFuelCardTicketToOpenNewFuelCardTicketAPI, toSaveFuelTransactionBody } from './APITransformers';
import type { CardProviderOption, FuelCardMatching, ZendeskTicket } from './Definitions/Types';

export class FuelCardRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getTransactionsReport = (period: DateRange, vehicleGroupPaths: string[]): Promise<Blob> => {
    const tz = TimeZone.getEncodedTimeZone();
    const requestData: IRequest = {
      endpoint: `/fuel/transactions/report?begin=${period.start}&end=${period.end}&tz=${tz}&vehicleGroupPaths=${vehicleGroupPaths.join(',')}`,
      method: HttpMethod.GET,
      responseType: 'arraybuffer',
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data]));
  };

  public saveFuelTransaction = (body: FuelTransactionBodyContent): Promise<void> => {
    const transactionBody = toSaveFuelTransactionBody(body);
    const requestData: IRequest = {
      endpoint: 'fuel/transactions',
      method: HttpMethod.POST,
      body: JSON.stringify(transactionBody),
    };

    return this.requester.request(requestData).then();
  };

  public getFuelVehicles = (): Promise<FuelCardVehicle[]> => {
    const requestData: IRequest = {
      endpoint: '/fuel/vehicles',
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => fuelVehiclesTransformer(response.data));
  };

  public updateFuelVehicles = (vehicles: FuelCardVehicle[]): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/vehicles',
      method: HttpMethod.PATCH,
      body: JSON.stringify(
        vehicles.map(({ id, refConsumption, integrationId }) => ({
          id,
          ref_fuel_consumption: refConsumption,
          integration_id: integrationId,
        }))
      ),
    };

    return this.requester.request(requestData).then();
  };

  public requestIntegration = (cardProvider: CardProviderOption, cnpj?: string, email?: string): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/fuel-card-integration',
      method: HttpMethod.POST,
      body: JSON.stringify({ card_provider: cardProvider, cnpj, email }),
    };

    return this.requester.request(requestData).then();
  };

  public updateManualFeatureFlag = (): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/featureFlag',
      method: HttpMethod.PATCH,
      body: JSON.stringify({ flag_name: 'fuel-card-manual' }),
    };

    return this.requester.request(requestData).then();
  };

  public loadFuelCardMatchings = (fuelCardStatus: string): Promise<FuelCardMatching[]> => {
    const requestData: IRequest = {
      endpoint: `/fuel/card/associations?fuelCardStatus=${fuelCardStatus}`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => fuelCardMatchingAPIToFuelCardMatching(response.data));
  };

  public saveFuelCardMatching = (id: UUID, licensePlate?: string): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/card/association',
      method: HttpMethod.PUT,
      body: JSON.stringify({
        id: id,
        license_plate: licensePlate,
      }),
    };

    return this.requester.request(requestData).then();
  };

  public openNewFuelCardTicket = (zendeskTicket: ZendeskTicket): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/fuelcard/ticket',
      method: HttpMethod.POST,
      body: JSON.stringify(openNewFuelCardTicketToOpenNewFuelCardTicketAPI(zendeskTicket)),
    };

    return this.requester.request(requestData).then();
  };

  public deactivateFuelCards = (ids: UUID[]): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/card/deactivate',
      method: HttpMethod.PUT,
      body: JSON.stringify({ ids: ids }),
    };

    return this.requester.request(requestData).then();
  };

  public deleteTransactions = (transactionIds: string[]): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/transactions/deactivate',
      method: HttpMethod.PUT,
      body: JSON.stringify({ transaction_ids: transactionIds }),
    };

    return this.requester.request(requestData).then();
  };

  public activateFuelCards = (ids: UUID[]): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/card/activate',
      method: HttpMethod.PUT,
      body: JSON.stringify({ ids: ids }),
    };

    return this.requester.request(requestData).then();
  };
}

export default FuelCardRequester;
